/**
 * This module provides a configured instance of axios for making API requests.
 *
 * It centralizes the API communication layer, allowing for consistent
 * configuration of base URLs, headers, and interceptors for handling
 * requests and responses globally.
 *
 * @module apiClient
 * @author Snowfly
 * @version 1.0.0
 */
import axios from 'axios';
import {
    FETCH_REQUEST,
    FETCH_SUCCESS,
    FETCH_FAILURE,
    FETCH_STATS,
    ADD_BASIC_INFORMATION,
    INCREASE_NEWSIM_STEP,
    DECREASE_NEWSIM_STEP,
    ADD_AVATAR_DETAILS,
    INITIALIZE_NEWSIM,
    ADD_KEYWORD,
    FETCH_CURRENT,
    FETCH_KEYWORD,
    FETCH_KBCA,
    ADD_LEVELS,
    FETCH_CHARACTERS,
    FETCH_LEVEL_CLIPS,
    FETCH_TAKESIM,
    FETCH_SCREENING_KEYWORD,
    FETCH_INSURANCES,
    FETCH_PHYSICIAN_MAP_FORM,
    SELECTED_KBCA,
    FETCH_DISPOSITIONS,
    ADD_DISPOSITIONS,
    FETCH_ARTICLES,
    SELECTED_ARTICLE,
    SAVE_ADDRESS,
    FETCH_OPPORTUNITY,
    SAVE_OPPORTUNITY,
    FETCH_OPPBYNAME,
    FETCH_USERDATA,
    FETCH_TOTAL_ASSIGNMENTS,
    FETCH_EMPLOYEE_LIST,
    FETCH_MEMBERS_LIST,
    FETCH_ASSIGNMENTS,
    FETCH_GROUPS_LIST,
    FETCH_SELECTION_DATA,
    ADD_ASSIGNMENT,
    CHANGE_PRODUCT_ACCESS,
    FETCH_PRODUCTS,
    FETCH_SIM_DETAILS,
    SET_EDIT_SIM_STEP,
    FETCH_PERMISSIONS_LIST,
    UPLOAD_LEVELS,
    SAVE_ELAPSED_TIME,
    GENERATE_SIM,
    FETCH_USERS,
    ADD_NEW_LEVELS,
    ADD_CLIP_AUDIO,
    FETCH_SIM_LEVEL_USER_RESPONSE,
    FETCH_USER_FINAL_SCORE_RESPONSE,
    FETCH_SAVED_KEYWORDS,
    RESET,
    SAVE_CASE_VALUES,
    SAVE_USER,
    SET_PASS_FAIL_CRITERIA,
    FETCH_AUTO_POPULATE,
    FETCH_GROUP_LIST,
    FETCH_USER_DATA,
    NOTES_SAVE,
    FETCH_CURRENT_STATUS,
    DELETE_PREVIOUS_ASSIGNMENT_DETAILS,
    TOGGLE_RESULT_BUTTONS,
    SAVE_SIM_TITLE,
    SELECTED_SIM,
    CURRENT_AUDIO_PLAYING,
    AUDIO_PLAY,
    ADD_ASSESSOR_NOTES,
    SAVE_ASSESSOR_NOTES,
    SHOW_REVIEWER_BUTTON,
    FETCH_SCORES,
    ALL_LEVEL_AGENT_AUDIO_PROCESS,
    ADD_SCREEN_RECORDER_BLOB,
    SET_EDIT_SIM_DETAILS_STEP,
    RESET_STATS,
    DELETE_ASSIGNMENT,
    UPDATE_CURRENT_CLIP,
    RESTART_LEVEL_IN_TAKE_SIM,
    ENABLE_VIDEO_RECORDING,
    RESET_VIDEO_STATS,
    VIDEO_UPLOAD_STATUS,
    FETCH_ASSIGNMENT_COUNTS,
    FETCH_TRASHED_SIMS,
    SET_VIDEO_BLOB,
    CLOSE_CAPTIVATE_WINDOW,
    RESET_PASS_FAIL,
    RESET_PASSFAIL_MODAL,
    SIM_PRODUCT_TYPE,
    SAVE_WRAP_SECONDS,
    SNOWFLY_ID_UPDATED,
    SCREEN_RECORDING_VIDEO_UPDATED,
} from './simTypes';
import sims from '../../permissions/sims';
import admin from '../../permissions/admin';
import dashboard from '../../permissions/dashboard';
import config from '../../config';

/**
 * Action creator function for dispatching a fetch request action.
 * Returns an object containing the action type.
 *
 * @function fetchRequest
 * @returns {Object} An action object with a `type` property corresponding to the fetch request action.
 */
export const fetchRequest = () => ({
    type: FETCH_REQUEST,
});

/**
 * Action creator for a successful fetch operation.
 *
 * @function fetchSuccess
 * @param {any} data - The data retrieved from the fetch operation.
 * @returns {Object} An action object with the type `FETCH_SUCCESS` and the provided data as its payload.
 */
export const fetchSuccess = (data) => ({
    type: FETCH_SUCCESS,
    payload: data,
});

/**
 * Action creator function to handle successful fetching of statistics.
 *
 * @function fetchSuccessStats
 * @param {Object} data - The data payload containing fetched statistics.
 * @returns {Object} An action object with 'type' and 'payload' properties.
 */
export const fetchSuccessStats = (data) => ({
    type: FETCH_STATS,
    payload: data,
});

/**
 * A function that creates and returns an action object for fetching assignments.
 * The action contains a type and a payload used to update the state in a Redux reducer.
 *
 * @function
 * @param {Object} data - The data payload to be included in the action object,
 *      typically the assignment details or related information.
 * @returns {Object} An action object with a `type` indicating the
 *      FETCH_ASSIGNMENTS action and a `payload` containing the provided data.
 */
export const fetchAssignment = (data) => ({
    type: FETCH_ASSIGNMENTS,
    payload: data,
});

/**
 * Action creator function to fetch characters.
 *
 * @function fetchCharacters
 * @param {Object} data - The payload containing the data for the characters.
 * @returns {Object} Returns an action object with type `FETCH_CHARACTERS` and
 *      the provided payload.
 */
export const fetchCharacters = (data) => ({
    type: FETCH_CHARACTERS,
    payload: data,
});

/**
 * An action creator function that generates an action object to fetch clips.
 *
 * @function fetchClips
 * @param {Object} data - The data to be dispatched as the payload containing
 *                        information required for fetching level clips.
 * @returns {Object} An action object with:
 *                   - `type`: Identifier for the action, represented by
 *                          FETCH_LEVEL_CLIPS.
 *                   - `payload`: The data passed to the action.
 */
export const fetchClips = (data) => ({
    type: FETCH_LEVEL_CLIPS,
    payload: data,
});

/**
 * Action creator function for fetching KBCA data.
 *
 * @function fetchKbcaData
 * @param {Object} data - The data to be used as the payload for the action.
 * @returns {Object} An action object containing the action type and payload.
 */
export const fetchKbcaData = (data) => ({
    type: FETCH_KBCA,
    payload: data,
});

/**
 * Action creator function for fetching the current data.
 *
 * This function returns an action object containing the type `FETCH_CURRENT`
 * and a payload carrying the provided data. It is typically used in state
 * management for dispatching actions related to fetching current data updates.
 *
 * @param {Object} data - The data to be included in the payload of the action.
 * @returns {Object} An action object with a type of `FETCH_CURRENT` and the
 *      provided data as the payload.
 */
export const fetchCurrent = (data) => ({
    type: FETCH_CURRENT,
    payload: data,
});

/**
 * Action creator for handling fetch failure.
 * This function returns an action object with a type of `FETCH_FAILURE`
 * and includes a payload containing the provided data.
 *
 * @function
 * @param {any} data - The data to be included in the payload.
 * It typically holds error or failure details from a fetching operation.
 * @returns {Object} An action object with the type `FETCH_FAILURE`
 * and the provided data as the payload.
 */
export const fetchFailure = (data) => ({
    type: FETCH_FAILURE,
    payload: data,
});

/**
 * Function to create an action object for fetching insurance data.
 *
 * @function fetchInsurances
 * @param {Object} data - The data to be included in the payload of the action.
 * @returns {Object} An action object containing the type and payload properties.
 */
export const fetchInsurances = (data) => ({
    type: FETCH_INSURANCES,
    payload: data,
});

/**
 * Action creator for fetching a physician map.
 *
 * This function takes input data and returns an action object containing the type
 * used for identifying the fetch physician map action and a payload that holds
 * the provided data.
 *
 * @function fetchPhysicianMap
 * @param {Object} data - The data to be included in the fetch physician map
 *      action payload.
 * @returns {Object} Action object containing the type and payload for the
 *      fetch physician map action.
 */
export const fetchPhysicianMap = (data) => ({
    type: FETCH_PHYSICIAN_MAP_FORM,
    payload: data,
});

/**
 * Action creator function to fetch user data.
 *
 * @function
 * @param {Object} data - The data related to the user that needs to be fetched.
 * @returns {Object} An action object containing the type `FETCH_USERDATA`
 *      and the payload with the provided data.
 */
export const fetchUser = (data) => ({
    type: FETCH_USERDATA,
    payload: data,
});

/**
 * Creates an action to fetch selection data.
 *
 * @function fetchSelectionData
 * @param {Object} data - The data to set as the payload for the action.
 * @returns {Object} The action object with type `FETCH_SELECTION_DATA` and
 *      the provided payload.
 */
export const fetchSelectionData = (data) => ({
    type: FETCH_SELECTION_DATA,
    payload: data,
});

/**
 * Action creator function to fetch the total number of assignments.
 *
 * @function fetchAssignmentTotal
 * @param {Object} data - The data containing assignment details to be processed.
 * @returns {Object} An action object with a type of `FETCH_TOTAL_ASSIGNMENTS`
 *      and a payload containing the provided data.
 */
export const fetchAssignmentTotal = (data) => ({
    type: FETCH_TOTAL_ASSIGNMENTS,
    payload: data,
});

// add recorded audio to clip
/**
 * Action creator for adding audio to a clip.
 *
 * This function generates an action object with the type `ADD_CLIP_AUDIO`,
 * carrying the provided data payload.
 *
 * @function addAudioToClip
 * @param {Object} data - The payload containing audio data to be added to the clip.
 * @returns {Object} An action object with type `ADD_CLIP_AUDIO` and the provided
 *      payload.
 */
export const addAudioToClip = (data) => ({
    type: ADD_CLIP_AUDIO,
    payload: data,
});

/**
 * An action creator function to fetch products.
 *
 * The function takes in product data and returns
 * an action object with a type indicating `FETCH_PRODUCTS`
 * and a payload containing the provided data.
 *
 * @function fetchProducts
 * @param {Object} data - The data related to products.
 * @returns {Object} An action object with a type and payload.
 */
export const fetchProducts = (data) => ({
    type: FETCH_PRODUCTS,
    payload: data,
});

/**
 * Action creator function that generates an action to add basic information to
 *      the Redux store.
 *
 * @param {Object} data - The data payload containing the information to be added.
 * @param {Object} res - The server response object containing additional
 *      information, particularly the ID from `res.data.id`.
 * @returns {Object} The action object containing the `type`, `result`, and
 *      `payload` fields.
 * The `type` defines the kind of action being performed, `result` includes the
 *      ID from the response, and `payload` contains the data being updated.
 */
export const addBasicInfoRedux = (data, res) => ({
    type: ADD_BASIC_INFORMATION,
    result: res.data.id,
    payload: data,
});

/**
 * Action creator function for adding an assignment into Redux state.
 *
 * This function receives a response object, extracts the assignment data from it,
 * and returns an action object containing the type of action and the associated data.
 *
 * @param {Object} res - The response object containing the assignment data.
 * @param {Object} res.data - The data property of the response object which
 *      includes the assignment details.
 * @returns {Object} An action object with the specified type and data payload
 *      to be dispatched to the Redux store.
 */
export const addAssignmentIntoToRedux = (res) => ({
    type: ADD_ASSIGNMENT,
    result: res.data,
});

/**
 * Action creator function that returns an action object with the type property
 * set to INCREASE_NEWSIM_STEP. This action is typically used to indicate
 * the intention to increment or progress to the next simulation step in
 * a specific workflow or application logic.
 *
 * @function
 * @returns {Object} An action object with a type property.
 */
export const increaseStep = () => ({
    type: INCREASE_NEWSIM_STEP,
});

/**
 * Represents an action creator function that, when called, creates an action
 *      to decrease the simulation step in the application state.
 *
 * @returns {Object} An action object containing the type property set to
 *      `DECREASE_NEWSIM_STEP`.
 */
export const decreaseStep = () => ({
    type: DECREASE_NEWSIM_STEP,
});

/**
 * Function to create an action for adding avatar details.
 *
 * @function addAvatarDetails
 * @param {Object} response - The response object containing avatar details to
 *      be added.
 * @returns {Object} Action object containing the type and payload.
 */
export const addAvatarDetails = (response) => ({
    type: ADD_AVATAR_DETAILS,
    payload: response,
});

/**
 * Action creator for fetching a keyword.
 *
 * This function generates an action object that includes
 * the type of action and the associated payload data. It is
 * typically used in Redux applications to trigger the state
 * update for fetching a keyword.
 *
 * @function fetchKeyword
 * @param {any} data - The payload data to be associated with the action.
 * @returns {Object} - The action object containing the type and payload.
 */
export const fetchKeyword = (data) => ({
    type: FETCH_KEYWORD,
    payload: data,
});

/**
 * Creates an action object to fetch auto-populate data.
 *
 * @param {any} data - The data to be used as the payload for the action.
 * @returns {Object} An action object with type `FETCH_AUTO_POPULATE` and the
 *      provided payload.
 */
export const fetchAutoPopulateArray = (data) => ({
    type: FETCH_AUTO_POPULATE,
    payload: data,
});

/**
 * Action creator function that generates an action to save keywords.
 *
 * @function saveKeywords
 * @param {Object} data - The data representing the keywords to be saved.
 * @returns {Object} An action object containing the type `FETCH_SAVED_KEYWORDS`
 *      and the provided payload.
 */
export const saveKeywords = (data) => ({
    type: FETCH_SAVED_KEYWORDS,
    payload: data,
});

/**
 * Creates an action object to reset the state in a Redux store.
 *
 * @returns {Object} An action object with a type property set to RESET.
 */
export const reset = () => ({
    type: RESET,
});

/**
 * A function that creates an action object to reset statistics in the
 *      application state.
 *
 * @returns {Object} An action object with a type property set to RESET_STATS.
 */
export const resetStats = () => ({
    type: RESET_STATS,
});

/**
 * Adds keywords to the state by dispatching an action with the provided data.
 *
 * @function
 * @param {Object} data - The payload containing keywords to be added.
 * @returns {Object} The action object containing the type and payload.
 */
export const addKeywords = (data) => ({
    type: ADD_KEYWORD,
    payload: data,
});

/**
 * Action creator function for triggering the FETCH_TAKESIM action.
 * It generates an action object containing a type and payload.
 *
 * @function takeSim
 * @param {Object} data - The payload to be dispatched with the FETCH_TAKESIM
 *      action.
 * @returns {Object} An action object with `type` set to FETCH_TAKESIM and the
 *      provided `payload`.
 */
export const takeSim = (data) => ({
    type: FETCH_TAKESIM,
    payload: data,
});

/**
 * Action creator function for initializing a new simulation.
 *
 * This function returns an action object with the specified type to signify
 * the initialization process of a new simulation in the application state.
 *
 * @returns {Object} An action object containing the type property set to
 *      INITIALIZE_NEWSIM.
 */
export const initializeNewSim = () => ({
    type: INITIALIZE_NEWSIM,
});

/**
 * A function to create an action object for adding levels.
 * It encapsulates the provided data as the payload and associates it
 * with the type ADD_LEVELS.
 *
 * @function
 * @param {Object} data - The data to be included in the action's payload.
 * @returns {Object} An action object containing the type and payload.
 */
export const addLevels = (data) => ({
    type: ADD_LEVELS,
    payload: data,
});

/**
 * An action creator function that constructs an action to fetch the screening
 *      keyword.
 *
 * The generated action object includes a type property identifying the action
 * and a payload property containing the provided data.
 *
 * @function
 * @param {any} data - The data to be passed as the payload for fetching the
 *      screening keyword.
 * @returns {Object} The action object with type and payload properties.
 */
export const fetchScreeningKeyword = (data) => ({
    type: FETCH_SCREENING_KEYWORD,
    payload: data,
});

/**
 * Action creator for saving the selected KBCA (Key-Based Cluster Analysis) data.
 *
 * Generates an action object with the type 'SELECTED_KBCA' and includes the provided
 * data as the payload.
 *
 * @function
 * @param {any} data - The data to be included as the payload in the action object.
 * @returns {Object} An action object with the specified type and payload.
 */
export const saveSelectedKbca = (data) => ({
    type: SELECTED_KBCA,
    payload: data,
});

/**
 * Creates an action object to fetch dispositions.
 *
 * @function fetchDispositions
 * @param {Object} data - The data to be sent as the payload of the action.
 * @returns {Object} The action object containing a type and payload.
 */
export const fetchDispositions = (data) => ({
    type: FETCH_DISPOSITIONS,
    payload: data,
});

/**
 * Action creator function for adding dispositions.
 *
 * @function addDispositions
 * @param {Object} data - The data to be added as dispositions.
 * @returns {Object} Action object containing the type `ADD_DISPOSITIONS` and
 *      the provided payload.
 */
export const addDispositions = (data) => ({
    type: ADD_DISPOSITIONS,
    payload: data,
});

/**
 * A function that creates an action object for fetching articles.
 *
 * This function generates an action object with a specific type and payload
 * to be used in a Redux action workflow. It accepts data as an argument,
 * which populates the payload property of the returned action object.
 *
 * @function fetchArticle
 * @param {Object} data - The payload data to be included in the action object.
 * @returns {Object} An action object containing a type and payload property.
 */
export const fetchArticle = (data) => ({
    type: FETCH_ARTICLES,
    payload: data,
});

/**
 * Action creator for saving the selected article.
 *
 * This function creates and returns an action object that stores
 * the type of the action and the payload containing the selected article data.
 *
 * @function saveSelectedArticle
 * @param {Object} data - The data representing the selected article.
 * @returns {Object} An action object with the following structure:
 * - type: A constant that indicates the action type (SELECTED_ARTICLE).
 * - payload: The data of the selected article.
 */
export const saveSelectedArticle = (data) => ({
    type: SELECTED_ARTICLE,
    payload: data,
});

/**
 * Action creator function to save an address.
 *
 * This function generates an action object containing the type of action
 * and a payload with the address details to be saved.
 *
 * @function saveAddress
 * @param {Object} payload - The address data to be saved.
 * @returns {Object} The action object with the type `SAVE_ADDRESS` and the
 *      provided payload.
 */
export const saveAddress = (payload) => ({
    type: SAVE_ADDRESS,
    payload,
});

/**
 * Creates an action object for loading an opportunity.
 *
 * @function loadOpportunity
 * @param {Object} data - The data to be used as the payload for the action.
 * @returns {Object} Returns an action object containing the type `FETCH_OPPORTUNITY`
 *      and the provided payload.
 */
export const loadOpportunity = (data) => ({
    type: FETCH_OPPORTUNITY,
    payload: data,
});

/**
 * Action creator function to save an opportunity.
 *
 * @param {Object} data - The data object representing the opportunity to be saved.
 * @returns {Object} The action object containing the type `SAVE_OPPORTUNITY`
 *      and the provided data as the payload.
 */
export const saveOpportunity = (data) => ({
    type: SAVE_OPPORTUNITY,
    payload: data,
});

/**
 * Creates an action object for fetching opportunities by name.
 *
 * @function fetchOppByNameData
 * @param {Object} data - The payload containing the data necessary to fetch
 *      opportunities by name.
 * @returns {Object} - An action object with a type of `FETCH_OPPBYNAME` and
 *      the provided payload.
 */
export const fetchOppByNameData = (data) => ({
    type: FETCH_OPPBYNAME,
    payload: data,
});

/**
 * Creates an action object for fetching employee data.
 *
 * @function fetchEmployees
 * @param {Object} data - The data to be sent as the payload in the action.
 * @returns {Object} An action object containing the type and payload.
 */
export const fetchEmployees = (data) => ({
    type: FETCH_EMPLOYEE_LIST,
    payload: data,
});

/**
 * Action creator function for fetching members list.
 *
 * @function fetchMembers
 * @param {Object} data - The payload containing the data required for the action.
 * @returns {Object} The action object containing the type and payload.
 */
export const fetchMembers = (data) => ({
    type: FETCH_MEMBERS_LIST,
    payload: data,
});

/**
 * Constructs an action object to fetch a list of permissions.
 *
 * @function
 * @param {Object} data - The payload data to be sent with the action.
 * @returns {Object} An action object containing the type and payload.
 */
export const fetchPermissions = (data) => ({
    type: FETCH_PERMISSIONS_LIST,
    payload: data,
});

/**
 * Constructs an action to fetch groups.
 *
 * This function returns an action object used in a Redux application
 * to request and handle the fetching of a list of groups. The resulting
 * object includes a type identifier and the payload data that represents
 * the groups to be fetched.
 *
 * @function fetchGroups
 * @param {Object} data - The payload data containing the information
 * required to fetch the groups.
 * @returns {Object} An action object with the type `FETCH_GROUPS_LIST` and
 * the provided payload data.
 */
export const fetchGroups = (data) => ({
    type: FETCH_GROUPS_LIST,
    payload: data,
});

/**
 * An action creator function used to dispatch an action for changing product access.
 * This function returns an action object containing a type and a payload.
 *
 * @param {Object} data - The data required to update the product access.
 * @returns {Object} An action object containing the type of action (`CHANGE_PRODUCT_ACCESS`)
 *                   and the payload with the provided `data`.
 */
export const changeProductAccess = (data) => ({
    type: CHANGE_PRODUCT_ACCESS,
    payload: data,
});

/**
 * Action creator function to add SIM details in the Redux store.
 *
 * @function addSimDetailsRedux
 * @param {Object} data - The SIM details to be added to the state.
 * @returns {Object} A Redux action with type `FETCH_SIM_DETAILS` and the given payload.
 */
export const addSimDetailsRedux = (data) => ({
    type: FETCH_SIM_DETAILS,
    payload: data,
});
// SAVE elapsed time in take sim / per level

/**
 * Action creator function for setting the current simulation step
 * in the editing process.
 *
 * This function returns an action object used to signal the update
 * of the simulation step being edited. The returned object contains
 * the action type necessary for identifying this particular update
 * within the application's state management flow.
 *
 * @returns {Object} The action object containing the type property set to 'SET_EDIT_SIM_STEP'.
 */
export const setEditSimStep = () => ({
    type: SET_EDIT_SIM_STEP,
});

/**
 * Action creator function for setting the edit SIM details step.
 *
 * This function returns an object representing the action to set
 * the current step in editing SIM details. The action object contains
 * a type property used to identify the action in the reducer.
 *
 * @function
 * @returns {Object} The action object with a type property set to SET_EDIT_SIM_DETAILS_STEP.
 */
export const setEditSimDetailsStep = () => ({
    type: SET_EDIT_SIM_DETAILS_STEP,
});

/**
 * Creates an upload levels action object.
 *
 * @function uploadLevelsResponse
 * @param {Object} data - The data payload for the upload levels action.
 * @returns {Object} An action object containing a type and payload.
 */
export const uploadLevelsResponse = (data) => ({
    type: UPLOAD_LEVELS,
    payload: data,
});

/**
 * Action creator for saving the elapsed time.
 *
 * This function generates an action object with the type `SAVE_ELAPSED_TIME`
 * and a payload containing the provided data.
 *
 * @function saveElapsedTime
 * @param {any} data - The data representing the elapsed time or associated information.
 * @returns {Object} The action object with a type of `SAVE_ELAPSED_TIME` and the provided data as the payload.
 */
export const saveElapsedTime = (data) => ({
    type: SAVE_ELAPSED_TIME,
    payload: data,
});

/**
 * Constructs an action object to save wrap seconds data.
 *
 * The function returns an object containing the action type `SAVE_WRAP_SECONDS`
 * and a payload holding the provided data.
 *
 * @function
 * @param {any} data - The data to be saved as the payload for the action.
 * @returns {Object} The action object with type and payload properties.
 */
export const saveWrapSeconds = (data) => ({
    type: SAVE_WRAP_SECONDS,
    payload: data,
});

/**
 * Function to generate a simulation response action.
 *
 * @function generateSimResponse
 * @param {any} data - The payload data to be included in the simulation action.
 * @returns {Object} The action object containing the type and payload.
 */
export const generateSimResponse = (data) => ({
    type: GENERATE_SIM,
    payload: data,
});


/**
 * Action creator function to add a screen recorder blob.
 *
 * @function addScreenRecorderBlob
 * @param {*} data - The blob data to be added.
 * @returns {Object} An action object containing the type and payload.
 */
export const addScreenRecorderBlob = (data) => ({
    type: ADD_SCREEN_RECORDER_BLOB,
    payload: data,
});

/**
 * Creates an action object for fetching a list of users.
 *
 * @function fetchUsers
 * @param {Object} data - The payload data containing the user information to be processed.
 * @returns {Object} An action object with a type of FETCH_USERS and the provided payload data.
 */
export const fetchUsers = (data) => ({
    type: FETCH_USERS,
    payload: data,
});

/**
 * Action creator function for fetching a group list.
 * Constructs an action object that can be dispatched to update the store
 * with the provided group list data.
 *
 * @function fetchGroup
 * @param {Object} data - The payload containing the group list data to be fetched.
 * @returns {Object} An action object with a type of `FETCH_GROUP_LIST` and the provided payload.
 */
export const fetchGroup = (data) => ({
    type: FETCH_GROUP_LIST,
    payload: data,
});

/**
 * A function that returns an action object to delete
 * the details of a previously saved assignment.
 *
 * @function deleteAssignmentDetails
 * @returns {Object} An action object containing the type property set to DELETE_PREVIOUS_ASSIGNMENT_DETAILS.
 */
export const deleteAssignmentDetails = () => ({
    type: DELETE_PREVIOUS_ASSIGNMENT_DETAILS,
});

/**
 * Action creator for successfully fetching assignment status.
 *
 * @param {Object} payload - The data containing the current status of the assignment.
 * @returns {Object} The action object containing type, payload, and reviewer status flag.
 */
export const getAssignmentStatusSuccess = (payload) => ({
    type: FETCH_CURRENT_STATUS,
    payload,
});

/**
 * Represents an action creator for toggling the state of result buttons.
 *
 * @function toggleResultButton
 * @param {Object} payload - The data or state to be passed with the action.
 * @returns {Object} An object with an action type of `TOGGLE_RESULT_BUTTONS` and the provided payload.
 */
export const toggleResultButton = (payload) => ({
    type: TOGGLE_RESULT_BUTTONS,
    payload,
});

/**
 * Action creator function for saving the simulation title.
 *
 * This function generates an action object to update the simulation title in the state.
 *
 * @param {string} data - The title of the simulation to be saved.
 * @returns {Object} An action object containing the type of action and the associated data as payload.
 */
export const saveSimTitle = (data) => ({
    type: SAVE_SIM_TITLE,
    payload: data,
});

/**
 * Defines an action creator function for saving the selected SIM.
 *
 * @function
 * @name saveSelectedSim
 * @param {Object} data - The payload containing information about the selected SIM.
 * @returns {Object} An action object containing the type `SELECTED_SIM` and the provided payload.
 */
export const saveSelectedSim = (data) => ({
    type: SELECTED_SIM,
    payload: data,
});

/**
 * Function to create an action object representing the current audio playing status.
 *
 * @function currentStatus
 * @param {Object} data - Data representing the current status or metadata of the audio playing.
 * @returns {Object} - Action object containing the type of action and payload with provided data.
 */
export const currentStatus = (data) => ({
    type: CURRENT_AUDIO_PLAYING,
    payload: data,
});

/**
 * Function that creates an action object for saving user data.
 *
 * @function addUserData
 * @param {object} data - The data to be processed or saved.
 * @param {object} res - Response object that contains user data.
 * @returns {object} Action object containing the type and result fields.
 */
export const addUserData = (data, res) => ({
    type: SAVE_USER,
    result: res.data,
});

/**
 * Creates an action to trigger audio playback.
 *
 * The function takes audio data as input and returns an object
 * with a type of AUDIO_PLAY and the provided data as the payload.
 *
 * @function
 * @param {Object} data - The data related to the audio play action.
 * @returns {Object} An action object containing the type and payload.
 */
export const audioPlay = (data) => ({
    type: AUDIO_PLAY,
    payload: data,
});

/**
 * Creates an action object to add assessor notes.
 *
 * @function
 * @param {Object} data - The data to be added as the payload for assessor notes.
 * @returns {Object} The action object containing the type of action and the provided payload.
 */
export const addAssessor = (data) => ({
    type: ADD_ASSESSOR_NOTES,
    payload: data,
});

/**
 * Represents an action creator function to save assessor notes.
 * This function constructs an action object with the specified type and payload.
 *
 * @function saveAssessor
 * @param {Object} data - The data containing assessor notes to be saved.
 * @returns {Object} Action object with a type of `SAVE_ASSESSOR_NOTES` and the provided payload.
 */
export const saveAssessor = (data) => ({
    type: SAVE_ASSESSOR_NOTES,
    payload: data,
});

/**
 * A function that creates an action object for showing reviewer buttons.
 *
 * @function handleShowReviewerButtons
 * @param {Object} payload - The data to be passed with the action.
 * @returns {Object} An action object containing the type `SHOW_REVIEWER_BUTTON` and the provided payload.
 */
export const handleShowReviewerButtons = (payload) => ({
    type: SHOW_REVIEWER_BUTTON,
    payload,
});

/**
 * Function to create an action object for fetching scores.
 *
 * @function fetchScore
 * @param {Object} data - The payload containing data required for fetching scores.
 * @returns {Object} An action object with the type `FETCH_SCORES` and the provided payload.
 */
export const fetchScore = (data) => ({
    type: FETCH_SCORES,
    payload: data,
});

/**
 * Represents an action creator that is used to handle the state of agent audio
 * processing success across all levels.
 *
 * @function checkAllLevelAgentAudioSuccess
 * @param {Object} payload - The data associated with the success of the audio process.
 * @returns {Object} An action object containing the type and payload to handle the
 *                   corresponding process.
 */
export const checkAllLevelAgentAudioSuccess = (payload) => ({
    type: ALL_LEVEL_AGENT_AUDIO_PROCESS,
    payload,
});

/**
 * Action creator function to enable video recording.
 *
 * @function enableVideoRecord
 * @param {Object} data - The payload data to be dispatched with the action.
 * @returns {Object} An action object containing the type `ENABLE_VIDEO_RECORDING` and the provided payload `data`.
 */
export const enableVideoRecord = (data) => ({
    type: ENABLE_VIDEO_RECORDING,
    payload: data,
});

/**
 * Creates an action object for deleting an assignment.
 *
 * @function assignmentDelete
 * @param {Object} data - The data related to the assignment to be deleted.
 * @returns {Object} An action object containing the type and payload.
 */
export const assignmentDelete = (data) => ({
    type: DELETE_ASSIGNMENT,
    payload: data,
});

/**
 * A function that creates an action object for adding new levels to the application's state.
 * The returned object contains the action type and the payload data.
 *
 * @function addNewLevels
 * @param {Object} data - The data representing the new levels to be added.
 * @returns {Object} An action object with the type 'ADD_NEW_LEVELS' and the provided data as the payload.
 */
export const addNewLevels = (data) => ({
    type: ADD_NEW_LEVELS,
    payload: data,
});

//  call user response API endpoint
/**
 * Constructs an action object indicating a successful user response.
 *
 * @function userResponseSuccess
 * @param {Object} data - The data object representing the user's response.
 * @returns {Object} An action object containing the type of the action and the provided payload (user response data).
 */
export const userResponseSuccess = (data) => ({
    type: FETCH_SIM_LEVEL_USER_RESPONSE,
    payload: data,
});

/**
 * Action creator for handling the successful response of fetching the user's final score.
 *
 * @function userFinalScoreResponseSuccess
 * @param {Object} payload - The data payload containing the user's final score details.
 * @returns {Object} An action object with type 'FETCH_USER_FINAL_SCORE_RESPONSE' and the provided payload.
 */
export const userFinalScoreResponseSuccess = (payload) => ({
    type: FETCH_USER_FINAL_SCORE_RESPONSE,
    payload,
});

/**
 * A function that creates an action object for saving case values.
 *
 * @function saveCaseVaules
 * @param {Object} payload - The payload data to be stored in the action object.
 * @param {number} textCount - The count of text elements to be saved.
 * @param {number} otherCount - The count of other elements to be saved.
 * @param {Object} resultScreenInputData - Input data for the result screen.
 * @returns {Object} The action object containing the provided data and the action type.
 */
export const saveCaseVaules = (
    payload,
    textCount,
    otherCount,
    resultScreenInputData,
) => ({
    type: SAVE_CASE_VALUES,
    payload,
    textCount,
    otherCount,
    resultScreenInputData,
});

/**
 * Action creator function for setting the pass/fail criteria success action.
 *
 * @function setPassFailCriteriaSuccess
 * @param {Object} payload - The data to be used as the payload for the action.
 * @returns {Object} Returns an action object with a type of `SET_PASS_FAIL_CRITERIA`
 * and the provided payload.
 */
export const setPassFailCriteriaSuccess = (payload) => ({
    type: SET_PASS_FAIL_CRITERIA,
    payload,
});

/**
 * Action creator function for initiating a Redux action
 * to fetch user data.
 *
 * @function fetchUserDataRedux
 * @param {Object} data - The user data to be used as the payload.
 * @returns {Object} An action object containing the type of
 * the action and the payload with the user data.
 */
export const fetchUserDataRedux = (data) => ({
    type: FETCH_USER_DATA,
    payload: data,
});

/**
 * Action creator function for saving notes.
 *
 * @function saveNotes
 * @param {Object} data - The data representing the notes to save.
 * @returns {Object} Action object containing the type and payload.
 */
export const saveNotes = (data) => ({
    type: NOTES_SAVE,
    payload: data,
});

/**
 * Action creator function to update the current clip.
 *
 * @function updateCurrentClip
 * @param {Object} payload - The data to update the current clip with.
 * @returns {Object} The action object containing the type and payload.
 */
export const updateCurrentClip = (payload) => ({
    type: UPDATE_CURRENT_CLIP,
    payload,
});

/**
 * A function that creates an action object for restarting the level in the intake simulation.
 *
 * This function is typically dispatched to reset the current level of the intake simulation
 * to its initial state. It generates an action object with a specific type that can be
 * handled by a reducer in a state management system, such as Redux.
 *
 * @function restartLevelInTakeSim
 *
 * @returns {Object} An action object with the `type` property set to `RESTART_LEVEL_IN_TAKE_SIM`.
 */
export const restartLevelInTakeSim = () => ({
    type: RESTART_LEVEL_IN_TAKE_SIM,
});

/**
 * Creates an action to reset video statistics.
 *
 * This function returns an action object containing the type
 * `RESET_VIDEO_STATS`. It can be dispatched to reset the state
 * associated with video statistics in the application.
 *
 * @function resetVideoStats
 *
 * @returns {Object} The action object with a `type` property set to `RESET_VIDEO_STATS`.
 */
export const resetVideoStats = () => ({
    type: RESET_VIDEO_STATS,
});

/**
 * Action creator function for saving the status of a video upload.
 *
 * @function saveVideoStatus
 * @param {Object} data - The data representing the status of the video upload.
 * @returns {Object} An action object containing the type of action and the payload with the video upload status data.
 */
export const saveVideoStatus = (data) => ({
    type: VIDEO_UPLOAD_STATUS,
    payload: data,
});

/**
 * Fetches the count of assignments and generates an action object.
 *
 * @function getAssignmentsCount
 * @param {Object} data - The input object containing the assignment data.
 * @param {Object} data.data - The specific payload data representing the assignment counts.
 * @returns {Object} An action object with `type` and `payload` properties.
 *                   The `type` property is a constant identifier for the action,
 *                   and the `payload` contains the processed assignment counts data.
 */
export const getAssignmentsCount = (data) => ({
    type: FETCH_ASSIGNMENT_COUNTS,
    payload: data.data,
});

/**
 * Action creator function to fetch trashed SIM cards.
 *
 * @function fetchTrashedSims
 * @param {Object} data - The data payload containing information related to trashed SIMs.
 * @returns {Object} The action object with type `FETCH_TRASHED_SIMS` and the provided payload.
 */
export const fetchTrashedSims = (data) => ({
    type: FETCH_TRASHED_SIMS,
    payload: data,
});

/**
 * Creates an action object to save a video blob.
 *
 * @function saveVideoBlob
 * @param {Blob} data - The video blob data to be saved.
 * @returns {Object} The action object containing the type `SET_VIDEO_BLOB` and the provided payload.
 */
export const saveVideoBlob = (data) => ({
    type: SET_VIDEO_BLOB,
    payload: data,
});

/**
 * A function that generates an action object to close the captivate window.
 *
 * @function saveCaptivateWindowRef
 * @param {any} data - The payload data to be passed in the action.
 * @returns {Object} An action object containing the type "CLOSE_CAPTIVATE_WINDOW" and the provided payload.
 */
export const saveCaptivateWindowRef = (data) => ({
    type: CLOSE_CAPTIVATE_WINDOW,
    payload: data,
});

/**
 * Action creator function for resetting the pass or fail status.
 *
 * @function resetPassFail
 * @returns {Object} Action object with a type of RESET_PASS_FAIL.
 */
export const resetPassFail = () => ({
    type: RESET_PASS_FAIL,
});

/**
 * Resets the state of the pass/fail modal.
 *
 * This function returns an action object with a specific type
 * that indicates the pass/fail modal should be reset to its
 * initial state.
 *
 * @function resetPassFailModal
 * @returns {Object} The action object containing the type property.
 */
export const resetPassFailModal = () => ({
    type: RESET_PASSFAIL_MODAL,
});

/**
 * Action creator function to save the current SIM product type.
 *
 * @function saveCurrentSimProductType
 * @param {any} data - The data representing the SIM product type to be saved.
 * @returns {Object} An action object with a type of `SIM_PRODUCT_TYPE` and the provided data as the payload.
 */
export const saveCurrentSimProductType = (data) => ({
    type: SIM_PRODUCT_TYPE,
    payload: data,
});

/**
 * Action creator function that generates an action to signal that the Snowfly ID has been updated.
 *
 * @function
 * @name snowflyIdUpdated
 * @param {any} data - The updated data associated with the Snowfly ID.
 * @returns {Object} The action object containing the type of action and updated payload data.
 */
export const snowflyIdUpdated = (data) => ({
    type: SNOWFLY_ID_UPDATED,
    payload: data,
});

/**
 * Action creator for updating the screen recording video.
 *
 * This function returns an action object with type `SCREEN_RECORDING_VIDEO_UPDATED`
 * and the provided data as its payload. It is used to update the state related
 * to screen recording video in the application.
 *
 * @param {Object} data - The data containing information about the updated
 * screen recording video.
 * @returns {Object} An action object with type `SCREEN_RECORDING_VIDEO_UPDATED`
 * and the provided data as its payload.
 */
export const screenRecordingVideoUpdated = (data) => ({
    type: SCREEN_RECORDING_VIDEO_UPDATED,
    payload: data,
});

/**
 * Dispatches an action to update the state with the provided screen recording
 *      video data.
 *
 * @function notifyScreenRecordingVideoUpdated
 * @async
 * @param {Object} data - The data related to the screen recording video that
 *      needs to be updated in the state.
 * @returns {Promise<void>} A promise that Redux dispatch function to send the
 *      action and resolves when the action is dispatched successfully.
 */
export const notifyScreenRecordingVideoUpdated = (data) => async (dispatch) => {
    dispatch(screenRecordingVideoUpdated(data));
};

/*export const addScreenRecorderBlob = (data) => ({
    type: ADD_SCREEN_RECORDER_BLOB,
    payload: data,
});*/

/**
 * An asynchronous function to fetch data from the API.
 * It dispatches actions to manage the state of the data request.
 *
 * @param {number} [limit=10] - The number of items to fetch per page.
 * @param {number} [page=0] - The current page number to fetch data for.
 * @param {string|null} [product=null] - Optional product identifier to filter the results.
 * @param {string|null} [title=null] - Optional title to filter the results.
 * @param {string|null} [sortBy=null] - Optional field name to sort the results by.
 * @param {string|null} [order=null] - Optional sort order for the results (e.g., 'asc' or 'desc').
 * @returns {Function} A function that dispatches relevant actions
 * to indicate the state of the API request and subsequently manages the response data.
 */
export const fetchData = (limit = 10, page = 0, product = null,
                          title = null, sortBy = null, order = null) => async (dispatch) => {
    const skip = limit * page;
    dispatch(fetchRequest());

    await axios(
        `${config.API_BASE_URL}/api/v1/sims/listing?skip=${skip}&limit=${limit}${product ? `&product=${product}` : ''
        }${title ? `&title=${title}` : ''}&sortBy=${sortBy}&order=${order}`, {
            headers: {
                Authorization: config.getToken(),
                task: sims.sim_list.view.task,
                type: sims.sim_list.view.type,
            },
        },
    )
        .then((res) => dispatch(fetchSuccess(res)))
        .catch((error) => {
            dispatch(fetchFailure(error));
        });
};

/**
 * Fetches simulation statistics from an API and dispatches appropriate actions based on the response.
 *
 * @function fetchStats
 * @param {string} simId - The ID of the simulation for which statistics are being fetched.
 * @param {string} [userId] - The optional ID of the user for fetching user-specific statistics.
 * @returns {function} A thunk function for Redux that dispatches actions for request, success, or failure states.
 */
export const fetchStats = (simId, userId) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios(`${config.API_BASE_URL}/api/v1/sims/stats?sim_id=${simId}${userId ? `&user_id=${userId}` : ''}`, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => dispatch(fetchSuccessStats(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Asynchronous action creator for updating simulation statistics.
 * Sends a POST request to the server with the provided simulation and level details.
 * Dispatches specific actions depending on the response of the request.
 *
 * @function updateStats
 * @param {string} simId - The unique identifier of the simulation.
 * @param {string} simLevelId - The unique identifier of the simulation level.
 * @param {string} status - The current status of the simulation (e.g., "in-progress", "completed").
 * @param {string} mode - The mode in which the simulation is running (e.g., "single-player", "multiplayer").
 * @param {number} [activeIndex=0] - The index of the currently active level or section in the simulation.
 * @param {Date | string} levelStart - The timestamp indicating when the current level started.
 * @param {boolean} [updateIndexBool=false] - A flag indicating whether to update the active index.
 * @returns {Function} A thunk function that handles the asynchronous operation.
 */
export const updateStats = (
    simId,
    simLevelId,
    status,
    mode,
    activeIndex = 0,
    levelStart,
    updateIndexBool = false,
) => async (dispatch) => {
    const body = {
        simId,
        simLevelId,
        mode,
        status,
        activeIndex,
        levelStart,
        updateIndexBool,
    };
    dispatch(fetchRequest);
    await axios
        .post(`${config.API_BASE_URL}/api/v1/sims/stats`, body, {
            headers: {
                Authorization: config.getToken(),
                task: null,
                type: null,
            },
        })
        .then((res) => dispatch(fetchSuccess(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Fetches level clips based on the provided simulation level ID and optional recordings.
 * Dispatches actions to handle the request, the response, and potential errors.
 *
 * @param {string} simLevelId - The simulation level identifier to fetch clips for.
 * @param {Array<string>} [recordings=[]] - Optional array of recording identifiers to filter the results.
 * @returns {Function} A thunk function that dispatches actions for the fetch lifecycle.
 */
export const fetchLevelClips = (simLevelId, recordings = []) => async (dispatch) => {
    dispatch(fetchRequest);
    let url = `${config.API_BASE_URL}/api/v1/sims/take-sim/details?simLevelId=${simLevelId}`;
    if (recordings?.length) {
        let recStr = recordings.join(',').trim();
        if (recStr.length) {
            url += '&recordings=' + recStr;
        }
    }
    await axios(
        url, {
            headers: {
                Authorization: config.getToken(),
                task: null,
                type: null,
            },
        },
    )
        .then((res) => dispatch(fetchClips(res)))
        .catch((error) => {
            dispatch(fetchFailure(error));
            dispatch(fetchClips({
                data: {},
            }));
        });
};

/**
 * A Redux thunk action creator that fetches KBCA (Knowledge Base Content Assistance) data from a specific endpoint.
 * It dispatches an appropriate sequence of actions during the fetch lifecycle.
 *
 * @function
 * @param {string} word - The search keyword used to query the KBCA API.
 * @returns {Function} A thunk function that uses the Redux dispatch function to handle asynchronous operations.
 *
 * @async
 * @throws Will dispatch an error action in case of a request failure.
 */
export const fetchKbca = (word) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios(`${config.MOCK_URL}/kbca?q=${word}`, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => dispatch(fetchKbcaData(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Asynchronous Redux action creator that fetches the current simulation data
 * based on the provided identifier and dispatches appropriate actions
 * depending on the request's success or failure.
 *
 * @param {string} id - The unique identifier for the simulation to be fetched.
 * @returns {Function} Dispatch function to be executed by Redux Thunk middleware.
 */
export const fetchCurrentSim = (id) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios.get(`${config.API_BASE_URL}/api/v1/sims/current-sim?id=${id}`, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => dispatch(fetchCurrent(res.data)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * A function to fetch the list of characters asynchronously.
 *
 * This function dispatches an initial fetch request action,
 * performs an API call to retrieve characters, and then
 * dispatches a success or failure action based on the result of the API call.
 *
 * The API call includes authorization headers and may handle errors.
 *
 * @function
 * @async
 */
export const fetchCharacterList = () => async (dispatch) => {
    dispatch(fetchRequest);
    await axios(`${config.API_BASE_URL}/characters`, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => dispatch(fetchCharacters(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Asynchronous action to fetch a list of products from the API.
 *
 * This function utilizes Redux Thunk to handle asynchronous operations. It
 * dispatches a fetch request action before making an API call, and based on
 * the API response or error, it dispatches either a success or failure action.
 *
 * The API request is made to the endpoint `${config.API_BASE_URL}/api/v1/product`
 * and includes an Authorization header for authentication.
 *
 * Actions Dispatched:
 * - `fetchRequest` before starting the API call.
 * - `fetchProducts` with the response data on a successful API call.
 * - `fetchFailure` with the error on a failed API call.
 *
 * @function
 * @async
 * @returns {Promise<void>} Resolves when the fetch operation completes.
 */
export const fetchProductList = () => async (dispatch) => {
    dispatch(fetchRequest);
    await axios.get(`${config.API_BASE_URL}/api/v1/product`, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => dispatch(fetchProducts(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Asynchronous function to dispatch an action for adding basic information.
 * It sends a POST request to the server with the provided data.
 *
 * @function
 * @async
 * @param {Object} data - The data payload to be sent in the POST request body.
 * @param {Object} requestData - Additional request data to be handled within Redux action dispatch.
 * @returns {Function} A thunk function that interacts with the Redux dispatch.
 *
 * @throws Will dispatch a fetchFailure action if the API request fails.
 */
export const addBasicInfo = (data, requestData) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios.post(`${config.API_BASE_URL}/api/v1/sims/basicInfo`, data, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => dispatch(addBasicInfoRedux(requestData, res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Logs error data to the server by dispatching appropriate actions and making an asynchronous POST request.
 *
 * @function logError
 * @param {Object} data - The error data to be logged.
 * @param {Object} requestData - Additional request-specific data.
 * @returns {Function} A thunk function to be executed by Redux Thunk middleware.
 */
export const logError = (data, requestData) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios.post(
        `${config.API_BASE_URL}/api/v1/sims/log-error`,
        {
            ...data,
            ...requestData ?? {}
        }, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * fetchCreateKeywords is a thunk action creator that performs an asynchronous fetch request
 * to retrieve layout data and dispatches corresponding actions based on the request outcome.
 *
 * @param {string|null} simId - An optional simulation ID used as a query parameter in the API request.
 * @returns {Function} - A thunk function that takes 'dispatch' as an argument
 * and manages the dispatch of actions based on the API response.
 */
export const fetchCreateKeywords = (simId = null) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios.get(`${config.API_BASE_URL}/api/v1/layoutData/fetch${simId ? `?sim_id=${simId}` : ''}`, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => dispatch(fetchKeyword(res.data.layoutString)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Asynchronous action creator to fetch and populate layout data for a given simulation ID.
 *
 * This function dispatches a request action, makes an API call to fetch layout data
 * using the provided simulation ID, and then dispatches the resulting data or an error
 * response based on the outcome of the API call.
 *
 * @param {string} sim_id - The simulation ID used to fetch the layout data.
 * @returns {function} A thunk function that performs the asynchronous API call
 * and dispatches the appropriate actions.
 */
export const fetchAutoPopulate = (sim_id) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios.get(`${config.API_BASE_URL}/api/v1/layoutData/fetch?sim_id=${sim_id}`, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => dispatch(fetchAutoPopulateArray(res.data.layoutString)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Asynchronous action to fetch saved keywords for a given simulation ID.
 * Dispatches actions to manage the state of the request process:
 *  - Initiates a request dispatch.
 *  - On success, dispatches to save the fetched keywords.
 *  - On failure, dispatches error handling.
 *
 * @param {string} id - The unique identifier of the simulation for which keywords need to be fetched.
 * @returns {function} A thunk function that handles the asynchronous operation.
 */
export const fetchSavedKeywords = (id) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios.get(`${config.API_BASE_URL}/api/v1/sims/get-keywords?sim_id=${id}`, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => dispatch(saveKeywords(res.data.layoutString[0])))
        .catch((error) => dispatch(fetchFailure(error)));
};

// <--------------- fetch default keywords --------------->
/**
 * A function that fetches the default demographic data by making an HTTP GET request
 * to the specified API endpoint. The function dispatches actions to handle the response
 * or any encountered errors.
 *
 * @function fetchDefaultDemographics
 * @returns {Function} A thunk function that performs the asynchronous operation.
 *
 * Functionality:
 * 1. Constructs an HTTP GET request to the API endpoint using axios.
 * 2. Includes necessary headers such as Authorization.
 * 3. Dispatches the `takeSim` action with the layout data on a successful response.
 * 4. Dispatches the `fetchFailure` action with the error object on failure.
 */
export const fetchDefaultDemographics = () => (dispatch) => {
    // dispatch(fetchRequest);
    axios(`${config.API_BASE_URL}/api/v1/layoutData/fetch`, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => dispatch(takeSim(res.data.layoutString)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * An asynchronous function that fetches demographic data based on the provided SIM ID.
 * This function dispatches actions to manage the state of the operation, including initiating the
 * request, handling successful responses, and managing error conditions.
 *
 * @param {string} simId - The identifier of the SIM whose demographics are to be fetched.
 * @returns {Function} A thunk function that performs the asynchronous operation and dispatches necessary actions.
 */
export const fetchDemographics = (simId) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios(`${config.API_BASE_URL}/api/v1/sims/get-keywords?sim_id=${simId}`, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => {
            if (res.data.layoutString.length) {
                dispatch(
                    takeSim(res.data.layoutString),
                );
            } else {
                dispatch(fetchFailure({}));
                dispatch(fetchDefaultDemographics());
            }
        })
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * An asynchronous function that dispatches actions to fetch and handle simulation data.
 *
 * This function initiates a fetch request by dispatching the `fetchRequest` action.
 * It performs an HTTP GET request to the `/takeSim` endpoint using the Axios library.
 * The request includes authorization and additional headers as defined in the configuration.
 * Upon a successful response, it dispatches the `fetchCurrent` action with the response data.
 * If the request fails, it dispatches the `fetchFailure` action with the error object.
 *
 * @function fetchTakeSim
 * @returns {Function} A thunk function that handles asynchronous dispatching.
 */
export const fetchTakeSim = () => async (dispatch) => {
    dispatch(fetchRequest);
    await axios(`${config.API_BASE_URL}/takeSim`, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => dispatch(fetchCurrent(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * An asynchronous Redux thunk action to fetch screening data.
 * It dispatches a request action before making an HTTP GET request
 * to retrieve screening information from a configured API endpoint.
 * On success, it dispatches an action with the response data.
 * On failure, it dispatches an action with the error encountered.
 *
 * @function fetchScreening
 * @returns {Function} A thunk function that performs the asynchronous operation and dispatches actions.
 */
export const fetchScreening = () => async (dispatch) => {
    dispatch(fetchRequest);
    await axios(`${config.MOCK_URL}/screening`, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => dispatch(fetchScreeningKeyword(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Asynchronous action creator function responsible for fetching physician map form data.
 * Dispatches actions to handle request initiation, success response, and failure response.
 *
 * The function makes an API call to retrieve physician map form data from the specified endpoint.
 * On successful response, it dispatches the `fetchPhysicianMap` action with the retrieved data.
 * On failure, it dispatches the `fetchFailure` action with the encountered error.
 *
 * @function fetchPhysicianMapForm
 * @returns {Function} A thunk function that handles the asynchronous API call and dispatches relevant actions.
 */
export const fetchPhysicianMapForm = () => async (dispatch) => {
    dispatch(fetchRequest);
    await axios(`${config.MOCK_URL}/physicianMapForm`, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => dispatch(fetchPhysicianMap(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/*export const fetchInsurancesLookup = () => async (dispatch) => {
    dispatch(fetchRequest);
    await axios(`${config.MOCK_URL}/insurances`, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => dispatch(fetchInsurances(res)))
        .catch((error) => dispatch(fetchFailure(error)));
export const fetchDispositions = (data) => ({
  type: FETCH_DISPOSITIONS,
  payload: data,
});*/

/**
 * A function that asynchronously fetches disposition keywords from a remote
 *      endpoint and dispatches appropriate actions.
 *
 * This function initiates a fetch request by dispatching a `fetchRequest` action,
 *      then makes an HTTP GET request to retrieve disposition keywords from a
 *      specified URL. If the request is successful, it dispatches a
 *      `fetchDispositions` action with the response data. In the event of a
 *      failure, it dispatches a `fetchFailure` action with the encountered error.
 *
 * @function fetchDispositionKeywords
 * @async
 * @returns {Promise<void>} A promise that takes a Redux dispatch function used
 *      to dispatch actions and resolves when the specified actions are dispatched
 *      based on the result of the HTTP request.
 */
export const fetchDispositionKeywords = () => async (dispatch) => {
    dispatch(fetchRequest);
    await axios(`${config.MOCK_URL}/dispositions`, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => dispatch(fetchDispositions(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Asynchronous action creator to fetch articles based on search criteria.
 *
 * This function performs an asynchronous HTTP GET request to retrieve articles
 * that match the given search input. It dispatches actions to manage the request's
 * lifecycle, including initiating the request, handling a successful response,
 * and capturing any errors that may occur.
 *
 * @function
 * @param {string} searchData - The search string used to filter articles.
 * @returns {Function} A function that takes the dispatch method as an argument
 *                     and manages state updates based on the request's result.
 */
export const fetchArticles = (searchData) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios(`${config.MOCK_URL}/articles?q=${searchData}`)
        .then((res) => dispatch(fetchArticle(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Fetches opportunities based on a provided name.
 *
 * This function is a thunk action creator used with Redux to perform an asynchronous
 * operation of fetching opportunity data whose names match the given query word.
 * It dispatches `fetchRequest` before starting the fetch operation, then dispatches
 * `fetchOppByNameData` upon successful retrieval of data, or `fetchFailure` if
 * an error occurs during the request.
 *
 * @param {string} word - The query string used to search for opportunities by name.
 * @returns {Function} A thunk function that performs the async operation.
 */
export const fetchOppByName = (word) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios(`${config.MOCK_URL}/opportunitiesByName?q=${word}`)
        .then((res) => dispatch(fetchOppByNameData(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * An asynchronous action creator function to fetch opportunity data from a
 *      specified API endpoint and dispatch appropriate actions based on the
 *      result of the API call.
 *
 * This function performs the following operations:
 * 1. Dispatches a `fetchRequest` action to indicate the start of a fetch operation.
 * 2. Makes an HTTP GET request to the opportunities endpoint of the `MOCK_URL`
 *      specified in the configuration.
 * 3. Dispatches a `loadOpportunity` action with the response data if the request
 *      is successful.
 * 4. Dispatches a `fetchFailure` action with the error object if the request fails.
 *
 * @function fetchOpportunity
 * @returns {Function} A Redux thunk function that performs the asynchronous
 *      operation and dispatches actions.
 */
export const fetchOpportunity = () => async (dispatch) => {
    dispatch(fetchRequest);
    await axios(`${config.MOCK_URL}/opportunities`)
        .then((res) => dispatch(loadOpportunity(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Fetches a paginated and searchable list of employees from the server.
 *
 * @function fetchEmployeeList
 * @param {number} pageNumber - The current page number for pagination (0-indexed).
 * @param {string} [textSearch=''] - Optional search term to filter employees by name or other criteria.
 * @param {string} [sortBy=''] - Optional field name to sort the employee list by (e.g., "name", "age").
 * @param {string} [order=''] - Optional sort order, either "asc" for ascending or "desc" for descending.
 * @returns {Function} An asynchronous dispatch function that fetches the employee list,
 * handles actions for fetching, success, and failure states, and updates the state accordingly.
 */
export const fetchEmployeeList = (
    pageNumber,
    textSearch = '',
    sortBy = '',
    order = ''
) => async (dispatch) => {
    const limit = 10;
    const skip = limit * pageNumber;

    dispatch(fetchRequest);

    let args = [
        `skip=${skip}`,
        `limit=${limit}`,
    ];
    if (sortBy) {
        args.push(`sortBy=${sortBy}`);
    }
    if (order) {
        args.push(`order=${order}`);
    }
    if (textSearch) {
        args.push(`textSearch=${textSearch}`);
    }
    await axios(
        `${config.API_BASE_URL}/api/v1/employee/list?${args.join('&')}`,
        {
            headers: {
                Authorization: config.getToken(),
                task: admin.employee_list.view.task,
                type: admin.employee_list.view.type,
            },
        }
    )
        .then((res) => dispatch(fetchEmployees(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Asynchronous thunk action to fetch the list of members from a mock URL.
 *
 * This function dispatches the necessary actions to handle the request lifecycle:
 * - Dispatches `fetchRequest` before initiating the API call.
 * - Performs an API request to the endpoint specified in the application's
 *      configuration to retrieve the members list.
 * - Dispatches `fetchMembers` with the response data if the API call is successful.
 * - Dispatches `fetchFailure` with an error object if the API call fails.
 *
 * Uses Axios for HTTP requests and adheres to Redux's dispatch pattern.
 *
 * @function fetchMembersList
 * @returns {Function} A thunk function that handles the dispatch of actions.
 */
export const fetchMembersList = () => async (dispatch) => {
    dispatch(fetchRequest);
    await axios(`${config.MOCK_URL}/membersList`)
        .then((res) => dispatch(fetchMembers(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Fetches a list of assignments based on various filtering and sorting criteria.
 *
 * @function fetchAssignments
 * @param {number} [pageNumber=0] - The page number for pagination.
 * @param {string|null} [textSearch=null] - The search term to filter assignments
 *      by text or keywords.
 * @param {boolean} myAssignment - Indicates whether to fetch only the assignments
 *      assigned to the current user.
 * @param {string|null} [tab=null] - The current tab context. Possible values
 *      include 'MY_ASSIGNMENT', 'COMPLETED', or 'PENDING_REVIEW'.
 * @param {string|null} [orderBy=null] - The column or field by which the results
 *      should be sorted.
 * @param {string|null} [order=null] - The sort order for the results, either
 *      'asc' (ascending) or 'desc' (descending).
 * @param {number} skip - The number of records to skip for pagination.
 * @returns {Function} A thunk action that dispatches actions to manage API
 *      requests and responses for fetching assignments.
 */
export const fetchAssignments = (
    pageNumber = 0,
    textSearch = null,
    myAssignment,
    tab = null,
    orderBy = null,
    order = null,
    skip
) => async (dispatch) => {
    dispatch(fetchRequest());

    const limit = 10;
    let task = dashboard.all_assignments.view.task;
    let type = dashboard.all_assignments.view.type;

    if (tab === 'MY_ASSIGNMENT' || tab === 'COMPLETED') {
        task = sims.assignment.view.task;
        type = sims.assignment.view.type;
    } else if (tab === 'PENDING_REVIEW') {
        task = dashboard.review_assignments.view.task;
        type = dashboard.review_assignments.view.type;
    }

    await axios(
        `${config.API_BASE_URL}/api/v1/sims/assignment/list?page=${pageNumber}&skip=${skip}&limit=${limit}${textSearch ?
            `&textSearch=${textSearch}`
            : ''
        }&my_assignment=${myAssignment}${tab ? `&tab=${tab}` : ''}&orderBy=${orderBy}&order=${order}`,
        {
            headers: {
                Authorization: config.getToken(),
                task,
                type,
            },
        }
    )
        .then((res) => dispatch(fetchAssignment(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Redux-thunk action for fetching user data from the API.
 *
 * This function dispatches an initial fetch request action, performs an
 * asynchronous API call to retrieve user data count, and dispatches either
 * a success or failure action based on the outcome of the API request.
 *
 * The API request uses a configured base URL and requires an Authorization
 * token, along with specific task and type headers.
 *
 * @function
 * @returns {Function} A thunk function that performs the user data fetch operation.
 */
export const fetchUserData = () => async (dispatch) => {
    dispatch(fetchRequest);
    await axios(`${config.API_BASE_URL}/api/v1/users/count`, {
        headers: {
            Authorization: config.getToken(),
            task: dashboard.user_account.view.task,
            type: dashboard.user_account.view.type,
        },
    })
        .then((res) => dispatch(fetchUser(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * An asynchronous action creator that fetches the total number of assignments
 *      from a specified API endpoint.
 *
 * This function performs an HTTP GET request to retrieve the total assignments
 *      data and dispatches various actions based on the request's state.
 *
 * Actions dispatched:
 * - Dispatches `fetchRequest` to signal the beginning of the fetch operation.
 * - On success, dispatches `fetchAssignmentTotal` with the API response to store
 *      the assignments data.
 * - On failure, dispatches `fetchFailure` with the error to handle request failure.
 *
 * The API base URL is defined in the application's configuration object (`config.MOCK_URL`).
 *
 * @function fetchTotalAssignments
 * @returns {Function} A thunk action to be executed by Redux Thunk middleware.
 */
export const fetchTotalAssignments = () => async (dispatch) => {
    dispatch(fetchRequest);
    await axios(`${config.MOCK_URL}/totalAssignments`)
        .then((res) => dispatch(fetchAssignmentTotal(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Fetches a paginated list of groups based on the given parameters.
 *
 * @param {number} pageNumber - The current page number for pagination (default is 0).
 * @param {string|null} textSearch - The search string to filter groups based on their name or description (default is null).
 * @param {string} sortBy - The property to sort the groups by (default is an empty string).
 * @param {string} order - The sort order, either 'asc' for ascending or 'desc' for descending (default is an empty string).
 * @return {Function} - A dispatch function that performs the asynchronous operation of fetching the groups list.
 */
export const fetchGroupsList = (
    pageNumber = 0,
    textSearch = null,
    sortBy = '',
    order = ''
) => async (dispatch) => {
    dispatch(fetchRequest);
    const limit = 10;
    const skip = limit * pageNumber;
    await axios(
        `${config.API_BASE_URL}/api/v1/groups/listing?skip=${skip}&limit=${limit}${textSearch ? `&textSearch=${textSearch}` : ''}${sortBy ? `&sortBy=${sortBy}` : ''}${order ? `&order=${order}` : ''}`,
            {
            headers: {
                Authorization: config.getToken(),
                task: admin.groups.view.task,
                type: admin.groups.view.type,
            },
        }
    )
        .then((res) => dispatch(fetchGroups(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Asynchronous action creator function to fetch the permission list based on the
 *      provided roles.
 * Dispatches actions to manage the request lifecycle, including fetch initiation,
 *      success, and failure.
 * Makes an HTTP POST request to the permissions details API endpoint.
 *
 * @function fetchPermissionList
 * @param {Array} roles - An array of roles for which the permissions need to be
 *      fetched. Can be empty or null.
 * @returns {Function} A thunk that performs the asynchronous fetch operation and
 *      dispatches appropriate actions.
 */
export const fetchPermissionList = (roles) => async (dispatch) => {
    dispatch(fetchRequest);
    const companyId = JSON.parse(localStorage.getItem('user')).company_id;
    await axios.post(
        `${config.API_BASE_URL}/api/v1/permissions/details?company_id=${companyId}`,
        roles,
        {
            headers: {
                Authorization: config.getToken(),
                task: roles && roles.length ? null : admin.permissions.view.task,
                type: roles && roles.length ? null : admin.permissions.view.type,
            },
        }
    )
        .then((res) => dispatch(fetchPermissions(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};


/**
 * Fetches detailed information about a SIM card based on its ID.
 *
 * This asynchronous function dispatches a request action, performs an API call
 *      to retrieve the full SIM details from the server, and subsequently
 *      dispatches actions to update the state with fetched data or handle any
 *      errors that occur during the process.
 *
 * @param {string|number} id - Unique identifier of the SIM card to fetch details for.
 * @returns {Function} A redux-thunk function that performs the asynchronous request
 *      and dispatches necessary actions.
 */
export const fetchSimDetails = (id) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios.get(
        `${config.API_BASE_URL}/api/v1/sims/full-details?sim_id=${id}`,
        {
            headers: {
                Authorization: config.getToken(),
                task: sims.sim_list.update.task,
                type: sims.sim_list.update.type,
            },
        }
    )
        .then((res) => dispatch(addSimDetailsRedux(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Uploads levels data for a simulation.
 *
 * @function
 * @name uploadLevels
 * @param {Object} data - The data to be uploaded.
 * @param {string} id - The unique identifier of the simulation.
 * @param {number} levelCount - The number of levels to be uploaded.
 * @param {string} order - The order in which the levels should be uploaded.
 * @returns {Function} An asynchronous function that performs the upload operation
 *      and dispatches the corresponding actions.
 *
 * The function makes a POST request to the API endpoint to upload the levels' data,
 * dispatches a request action before the API call, and dispatches either a success or
 * failure action based on the response from the API. The authorization header is
 * included for authentication.
 */
export const uploadLevels = (data, id, levelCount, order) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios.post(
        `${config.API_BASE_URL}/api/v1/sims/upload-levels?sim_id=${id}&levelCount=${levelCount}&order=${order}`,
        data,
            {
            headers: {
                Authorization: config.getToken(),
                task: null,
                type: null,
            },
        }
    )
        .then((res) => dispatch(uploadLevelsResponse(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

// SAVE elapsed time in take sim / per level

/**
 * fetchUserList is a Redux Thunk action creator designed to fetch a list of users
 * asynchronously from the specified API endpoint. Upon invocation, it dispatches
 * a request action, performs an HTTP GET request to retrieve the user list,
 * and subsequently dispatches either a success or failure action based on the
 * outcome of the API call.
 *
 * The function utilizes Axios for making the HTTP request and sends an Authorization
 * token and other headers in the request. If the API request is successful, the
 * response data is processed and dispatched with the success action. If an error
 * occurs during the request, it dispatches a failure action with the error data.
 *
 * Note: It assumes that certain Redux actions (fetchRequest, fetchUsers, fetchFailure)
 * and configuration utilities (config.API_BASE_URL, config.getToken) are predefined
 * and accessible within the scope.
 */
export const fetchUserList = () => async (dispatch) => {
    dispatch(fetchRequest);
    await axios.get(
        `${config.API_BASE_URL}/api/v1/users/list`,
            {
            headers: {
                Authorization: config.getToken(),
                task: null,
                type: null,
            },
        }
    )
        .then((res) => dispatch(fetchUsers(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Asynchronous function to fetch the list of groups from the server.
 * Dispatches actions to indicate the request status and updates the store
 *      accordingly.
 *
 * This function performs the following steps:
 * 1. Dispatches the `fetchRequest` action to indicate the start of the fetching
 *      process.
 * 2. Sends a GET request to the specified API endpoint to retrieve the group
 *      listing.
 * 3. If the request is successful, dispatches the `fetchGroup` action with the
 *      response data.
 * 4. If an error occurs, dispatches the `fetchFailure` action with the error
 *      data.
 */
export const fetchGroupList = () => async (dispatch) => {
    dispatch(fetchRequest);
    await axios.get(
        `${config.API_BASE_URL}/api/v1/sims/group/listing`,
        {
            headers: {
                Authorization: config.getToken(),
                task: null,
                type: null,
            },
        }
    )
        .then((res) => dispatch(fetchGroup(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Handles the asynchronous action of adding a new assignment.
 * Dispatches a request action, sends a POST request to the add assignment API,
 *      and updates the redux store with the response or an error message.
 *
 * @param {Object} data - The data object containing assignment details to be
 *      sent to the server.
 * @returns {Function} A thunk function that performs the asynchronous request
 *      and dispatches actions.
 */
export const addAssignment = (data) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios.post(
        `${config.API_BASE_URL}/api/v1/sims/assignment/add`,
        data,
        {
            headers: {
                Authorization: config.getToken(),
                task: sims.assignment.create.task,
                type: sims.assignment.create.type,
            },
        }
    )
        .then((res) => dispatch(addAssignmentIntoToRedux(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Adds the status of an assignment by sending the provided data to the
 *      respective API endpoint and dispatches appropriate actions based on the
 *      API response or error.
 *
 * @function addAssignmentStatus
 * @param {Object} data - The data object containing assignment details to be
 *      sent to the API.
 * @returns {Function} - A thunk function that performs the asynchronous operation
 *      using dispatch.
 */
export const addAssignmentStatus = (data) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios.post(`${config.API_BASE_URL}/api/v1/sims/assignment/add`, data, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => dispatch(addAssignmentIntoToRedux(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Handles the asynchronous operation of sending user data and managing the
 *      application state through redux dispatch actions.
 *
 * @function fetchUserResponse
 * @param {Object} data - The user data to be sent to the specified API endpoint.
 * @returns {Function} A thunk function that performs the operation. Dispatches
 *          `fetchRequest` to indicate the start of the operation, sends an
 *          HTTP POST request, and subsequently dispatches either
 *          `userResponseSuccess` or `fetchFailure` depending on the outcome
 *          of the request. In case of failure, it displays an alert with the error.
 */
export const fetchUserResponse = (data) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios.post(`${config.API_BASE_URL}/api/v1/sims/save-take-sim-details`, data, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => dispatch(userResponseSuccess(res)))
        .catch((error) => {
            dispatch(fetchFailure(error));
            alert(error);
        });
};

// <------------------------ Fetch final score response ------------------------->

/**
 * Asynchronous action function to fetch the final score response of a user.
 *
 * This function triggers a dispatch to indicate the start of a fetch request.
 * It sends a POST request to the specified API endpoint using the provided `id`
 *      and optional `payload`.
 * On a successful response, it dispatches a success action with the retrieved data.
 * On a failure, it dispatches an error action with the encountered error.
 *
 * @function
 * @param {string} id - The unique identifier of the scorecard to fetch.
 * @param {Object} [payload={}] - Optional payload data to include in the POST
 *      request.
 * @returns {Function} A thunk function that handles the asynchronous request
 *      and related dispatch actions.
 */
export const userFinalScoreResponse = (id, payload = {}) => async (dispatch) => {
    dispatch(fetchRequest);
    axios({
        url: `${config.API_BASE_URL}/api/v1/sims/get-scorecard/${id}`,
        method: 'POST',
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
        data: payload,
    })
        .then((res) => dispatch(userFinalScoreResponseSuccess(res.data)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Asynchronous action to save a user's data by either creating a new user or
 *      updating an existing one.
 * Sends an HTTP POST request to the configured API endpoint and dispatches
 *      appropriate actions to update the application state.
 *
 * @function saveUser
 * @param {object} data - The user data to be saved. Includes user details and
 *      determines whether to create or update based on the presence of an `_id`
 *      property.
 * @returns {function} A Redux Thunk function that dispatches actions and performs
 *      an asynchronous request.
 *
 * Dispatches:
 * - fetchRequest: Action dispatched before the API call, indicating the start of
 *      the request.
 * - addUserData: Action dispatched after a successful request, containing the
 *      user data and the server response.
 * - fetchFailure: Action dispatched when the API call fails, passing along the
 *      error information.
 *
 * @throws {Error} Throws an error if the API request fails.
 */
export const saveUser = (data) => async (dispatch) => {
    const task = data._id ? admin.employee_list.update.task : admin.employee_list.create.task;
    const type = data._id ? admin.employee_list.update.type : admin.employee_list.create.type;
    dispatch(fetchRequest);
    await axios.post(`${config.API_BASE_URL}/api/v1/users/create`, data, {
        headers: {
            Authorization: config.getToken(),
            task,
            type,
        },
    })
        .then((res) => dispatch(addUserData(data, res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * saveUserSettings is an asynchronous action creator that saves user settings
 *      by making a POST request to the specified API endpoint. It dispatches
 *      actions based on the request's success or failure.
 *
 * @param {Object} data - The user settings data to be sent in the POST request.
 * @returns {Function} A thunk function that handles the asynchronous API call
 *      and dispatches related actions.
 *
 * - Dispatches `fetchRequest` before initiating the API call to indicate that
 *      the request is in progress.
 * - On successful response, dispatches `addUserData` with the provided data
 *      and the server's response.
 * - On error, dispatches `fetchFailure` with the error object to handle the
 *      failure case.
 */
export const saveUserSettings = (data) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios.post(`${config.API_BASE_URL}/api/v1/users/create`, data, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => dispatch(addUserData(data, res)))
        .catch((error) => dispatch(fetchFailure(error)));
};
/**
 * Asynchronous action to fetch and dispatch user result data.
 *
 * This function makes a POST request to the specified API endpoint, including
 *      optional user ID parameters if provided, and dispatches actions based on
 *      the success or failure of the request. The Redux dispatch function is
 *      used to handle state updates.
 *
 * @param {Object} data - The request payload to send in the POST request body.
 * @param {string} [agent_user_id] - Optional user ID to be included as a query
 *      parameter in the API request.
 * @returns {Function} A redux-thunk function that performs an asynchronous
 *      operation and dispatches necessary actions.
 */
export const viewResult = (data, agent_user_id) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios({
        url: `${config.API_BASE_URL}/api/v1/sims/result${agent_user_id ? `?user_id=${agent_user_id}` : ''}`,
        method: 'POST',
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
        data,
    })
        .then((res) => dispatch(userFinalScoreResponseSuccess(res.data)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Asynchronous action creator that sets the pass/fail criteria by sending a POST
 *      request to the specified API endpoint. Dispatches either a success action
 *      or a failure action based on the response received from the server.
 *
 * @function setPassFailCriteria
 * @param {Object} payload - The payload object containing data to be sent in the
 *      request.
 * @returns {Function} A function to be executed by Redux Thunk middleware, which
 *      performs the API call and dispatches the appropriate actions.
 */
export const setPassFailCriteria = (payload) => (dispatch) => {
    axios({
        url: `${config.API_BASE_URL}/api/v1/sims/set-criteria`,
        method: 'POST',
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
        data: payload,
    })
        .then((res) => dispatch(setPassFailCriteriaSuccess(res.data)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Retrieves the current user data based on the provided user ID and updates the
 *      application state.
 * If no user ID is provided, the default value of an empty string is used.
 *
 * This function dispatches various actions to manage the request process:
 * 1. Dispatches a fetch request action before initiating the API call.
 * 2. On success, it dispatches an action to store the fetched user data in Redux.
 * 3. On failure, it dispatches an action to manage the error state.
 *
 * @param {string} [userId=''] - The ID of the user whose data is to be fetched.
 *      If not specified, defaults to an empty string.
 * @returns {Function} A thunk function that interacts with the Redux dispatch
 *      mechanism.
 */
export const fetchCurrentUserData = (userId = '') => async (dispatch) => {
    dispatch(fetchRequest);
    await axios(`${config.API_BASE_URL}/api/v1/users/info?${userId ? `&user_id=${userId}` : ''}`, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => dispatch(fetchUserDataRedux(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Asynchronous action creator for saving notes.
 * This function handles the process of creating or updating a note by
 * dispatching necessary actions and making an API request.
 *
 * @function
 * @param {Object} data - The note data to be saved.
 * @param {string} [data.id] - The unique identifier of the note. Used to
 *      determine if the note is being created or updated.
 * @param {string} data.title - The title of the note.
 * @param {string} data.content - The content of the note.
 * @returns {Function} A thunk function that handles the asynchronous API request
 *      and dispatches actions.
 *
 * Dispatches:
 * - fetchRequest: Action to indicate that a fetch operation has started.
 * - saveNotes: Action to handle the response and save the note data.
 * - fetchFailure: Action to handle and dispatch any errors encountered during the
 *      request.
 */
export const notesSave = (data) => async (dispatch) => {
    const task = data.id ? dashboard.my_notepad.update.task : dashboard.my_notepad.create.task;
    const type = data.id ? dashboard.my_notepad.update.type : dashboard.my_notepad.create.type;
    dispatch(fetchRequest);
    await axios({
        url: `${config.API_BASE_URL}/api/v1/sims/notes`,
        method: 'POST',
        headers: {
            Authorization: config.getToken(),
            task,
            type,
        },
        data,
    })
        .then((res) => dispatch(saveNotes(res.data)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Fetches notes from the server.
 *
 * This asynchronous action makes a GET request to the API endpoint for notes
 * and dispatches appropriate actions based on the result of the request.
 * It dispatches a request action, and upon success, dispatches an action to save
 * the retrieved notes. If the request fails, it dispatches a failure action.
 *
 * @function
 * @param {Object} data - The data payload to be sent with the API request.
 * @returns {Function} A thunk function that performs the API call and dispatches
 *                      actions.
 */
export const fetchNotes = (data) => async (dispatch) => {
    dispatch(fetchRequest);
    axios({
        url: `${config.API_BASE_URL}/api/v1/sims/notes`,
        method: 'GET',
        headers: {
            Authorization: config.getToken(),
            task: dashboard.my_notepad.view.task,
            type: dashboard.my_notepad.view.type,
        },
        data,
    })
        .then((res) => dispatch(saveNotes(res.data)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Dispatches an action to delete a specific note by its ID from the server.
 * This is an asynchronous operation that makes a DELETE request to the API endpoint.
 * On success, dispatches an action to save the updated notes state.
 * On failure, dispatches an action to handle the error.
 *
 * @function deleteNotes
 * @async
 * @param {string} id - The unique identifier of the note to delete.
 * @returns {Function} A thunk function that handles the asynchronous delete operation.
 */
export const deleteNotes = (id) => async (dispatch) => {
    dispatch(fetchRequest);
    axios({
        url: `${config.API_BASE_URL}/api/v1/sims/notes/${id}`,
        method: 'DELETE',
        headers: {
            Authorization: config.getToken(),
            task: dashboard.my_notepad.delete.task,
            type: dashboard.my_notepad.delete.type,
        },
    })
        .then((res) => dispatch(saveNotes(res.data)))
        .catch((error) => dispatch(fetchFailure(error)));
};

// <------------------------- Get Assignment status -------------------------->

/**
 * Fetches the status of an assignment by its ID from the API and dispatches the
 *      appropriate action based on the response.
 *
 * @function getAssignmentStatus
 * @param {string} assignmentId - The unique identifier of the assignment whose
 *      status needs to be retrieved.
 * @returns {Function} A thunk function that performs the API call and dispatches
 *      actions accordingly.
 */
export const getAssignmentStatus = (assignmentId) => async (dispatch) => {
    axios({
        url: `${config.API_BASE_URL}/api/v1/sims/assignment/status/${assignmentId}`,
        method: 'GET',
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => {
            dispatch(getAssignmentStatusSuccess(res.data));
        })
        .catch((error) => dispatch(fetchFailure(error)));
};

// <--------------- toggle result buttons -------------------->



/**
 * Retrieves assessor notes for a specific ID by making an API request.
 * Dispatches a fetch request and handles the API response and errors accordingly.
 *
 * @function
 * @param {string} id - The unique identifier of the assessor notes to fetch.
 * @returns {Function} - A thunk function to dispatch actions.
 */
export const getAssessorNotes = (id) => async (dispatch) => {
    dispatch(fetchRequest);
    axios({
        url: `${config.API_BASE_URL}/api/v1/sims/get-assessor-notes/${id}`,
        method: 'GET',
        headers: {
            Authorization: config.getToken(),
            task: dashboard.assessor_notes.view.task,
            type: dashboard.assessor_notes.view.type,

        },
    })
        .then((res) => dispatch(saveAssessor(res.data)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Saves assessor notes for a given ID by sending a POST request to the API endpoint.
 *
 * @function SaveAssessorNotes
 * @param {string} id - The unique identifier for the assessor notes to be saved.
 * @param {Object} [payload={}] - Optional payload object containing the data to
 *      be sent in the request body.
 * @returns {Function} A Redux thunk action that performs the operation asynchronously.
 *
 * The function dispatches the `fetchRequest` action prior to making the API call.
 * If the API call succeeds, it dispatches `saveAssessor` with the response data.
 * If the API call fails, it dispatches `fetchFailure` with the error.
 */
export const SaveAssessorNotes = (id, payload = {}) => async (dispatch) => {
    dispatch(fetchRequest);
    axios({
        url: `${config.API_BASE_URL}/api/v1/sims/save-assessor-notes/${id}`,
        method: 'POST',
        headers: {
            Authorization: config.getToken(),
            task: dashboard.assessor_notes.view.task,
            type: dashboard.assessor_notes.view.type,
        },
        data: payload,
    })
        .then((res) => dispatch(saveAssessor(res.data)))
        .catch((error) => dispatch(fetchFailure(error)));
};
// <---------------- show reviewer button ------------------->

/**
 * Fetches a list of scores from the API based on provided parameters.
 * This function is an asynchronous Redux action that dispatches actions
 * for the request, success, and failure of the data-fetching process.
 *
 * @param {number} [pageNumber=0] - The page number for pagination purposes.
 * @param {string|null} [textSearch=null] - Optional search keyword for filtering
 *      results.
 * @param {boolean} finalResult - Indicates whether to filter by the final result.
 * @param {boolean} highestLevel - Indicates whether to filter by the highest level achieved.
 * @param {Object} filterData - An object containing filter data for the API request.
 * @param {string|null} [sortBy=null] - Optional parameter to specify the field
 *      to sort by.
 * @param {string|null} [sort=null] - Optional parameter for sorting order
 *      (e.g., "asc" or "desc").
 * @param {number} skip - The number of records to skip for pagination.
 * @returns {Function} A Redux thunk that handles the asynchronous API call and
 *                      dispatches actions for the request process.
 */
export const fetchScoreList = (
    pageNumber = 0,
    textSearch = null,
    finalResult = true,
    highestLevel= false,
    filterData= null,
    sortBy = null,
    sort = null,
    skip = 0
) => async (dispatch) => {
    dispatch(fetchRequest());
    const limit = 10;
    await axios.post(
        `${config.API_BASE_URL}/api/v1/sims/level/listing?page=${pageNumber}&skip=${skip}&limit=${limit}${textSearch ? `&textSearch=${textSearch}` : ''}&final_result=${finalResult}&highest_level=${highestLevel}&sortBy=${sortBy}&sort=${sort}`,
        filterData,
        {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
        }
    )
        .then((res) => dispatch(fetchScore(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

// <------------ Check all level agent audio status ------------>

/**
 * Executes a recurring check for audio processing status at all levels of an
 *      agent in a simulation.
 * This function dispatches actions to manage the state during the status check
 *      process.
 * If all audio processing is complete, it dispatches a success action.
 * If not, it continues checking periodically after a delay until processing is
 *      complete.
 *
 * @param {string} simId - The simulation identifier.
 * @param {string} [assignmentId] - The optional assignment identifier.
 * @param {string} [user_id] - The optional user identifier.
 * @returns {Function} A thunk function that performs the asynchronous operation,
 *                      dispatching actions as needed.
 */
export const checkAllLevelAgentAudio = (simId, assignmentId, user_id) => (dispatch) => {
    dispatch(fetchRequest);
    const delaySomeTime = (sec) => new Promise((resolve) => {
        setTimeout(() => {
            resolve();
        }, sec);
    });
    const getStatus = (simId, assignmentId, user_id) => {
        axios({
            url: `${config.API_BASE_URL}/api/v1/sims/agent/audio/status?simId=${simId}${assignmentId ? `&assignmentId=${assignmentId}` : ''}${user_id ? `&user_id=${user_id}` : ''}`,
            method: 'GET',
            headers: {
                Authorization: config.getToken(),
                task: null,
                type: null,
            },
        })
            .then((res) => {
                if (res.data.allAudioProcessed) {
                    dispatch(checkAllLevelAgentAudioSuccess(res.data.allAudioProcessed));
                } else {
                    dispatch(checkAllLevelAgentAudioSuccess(false));
                    delaySomeTime(6000).then(() => {
                        getStatus(simId, assignmentId, user_id);
                    });
                }
            })
            .catch((error) => dispatch(fetchFailure(error)));
    };
    getStatus(simId, assignmentId, user_id);
};

/**
 * Asynchronous action to save assignment notes for a specific assignment ID.
 *
 * This function sends a POST request to the backend API to either create or update
 * assignment notes, based on the value of the `isUpdate` parameter. The request includes
 * custom headers for authorization and task/type identification, as well as the provided data payload.
 *
 * @param {string} id - The unique identifier of the assignment for which the notes are being saved.
 * @param {Object} [payload={}] - The data to be sent in the request body. Contains the assignment note details.
 * @param {boolean} isUpdate - Flag indicating whether the operation is an update (`true`) or a create (`false`).
 * @returns {Function} A thunk that dispatches actions based on the success or failure of the request.
 */
export const saveAssignmentNotes = (id, payload = {}, isUpdate) => async (dispatch) => {
    dispatch(fetchRequest);
    axios({
        url: `${config.API_BASE_URL}/api/v1/sims/save-assignment-notes/${id}`,
        method: 'POST',
        headers: {
            Authorization: config.getToken(),
            task: isUpdate
                ? dashboard.notes_to_user.update.task
                : dashboard.notes_to_user.create.task,
            type: isUpdate
                ? dashboard.notes_to_user.update.type
                : dashboard.notes_to_user.create.type,
        },
        data: payload,
    })
        .then((res) => dispatch(saveAssessor(res.data)))
        .catch((error) => {
            dispatch(fetchFailure(error));
        });
};

/**
 * Asynchronous action creator to delete an assignment.
 *
 * This function sends a DELETE request to the specified API endpoint to
 * delete an assignment identified by its unique ID. It dispatches actions
 * to handle the request lifecycle including success or failure responses.
 * The process begins with a dispatch of the `fetchRequest` action.
 *
 * @param {string} id - The unique identifier of the assignment to delete.
 * @returns {Function} A thunk function to be executed by the Redux dispatcher.
 */
export const deleteAssignment = (id) => async (dispatch) => {
    dispatch(fetchRequest);
    axios({
        url: `${config.API_BASE_URL}/api/v1/sims/delete-assignment/${id}`,
        method: 'DELETE',
        headers: {
            Authorization: config.getToken(),
            task: sims.assignment.delete.task,
            type: sims.assignment.delete.type,
        },
    })
        .then(() => dispatch())
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Async function to handle the upload of a recorded video to a specified API
 *      endpoint.
 * Dispatches various actions during the process to manage the state.
 *
 * @function
 * @param {Object} data - The video file or associated payload to be uploaded.
 * @returns {Function} A redux-thunk function that performs the asynchronous
 *      operation.
 */
export const uploadRecordedVideo = (data) => async (dispatch) => {
    dispatch(fetchRequest);
    axios({
        url: `${config.API_BASE_URL}/api/v1/sims/upload-video`,
        method: 'POST',
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
        data,
    })
        .then((res) => dispatch(saveVideoStatus(res.data)))
        .catch((error) => dispatch(saveVideoStatus(error.response.data)));
};

/**
 * Action creator function to fetch the count of assignments from the API.
 * The function dispatches actions based on the API request state.
 * It first dispatches a request action, then makes an asynchronous call
 * to the assignments count endpoint, and based on the response, it either
 * dispatches the count data or handles any errors encountered.
 *
 * @function fetchAssignmentsCount
 * @returns {Function} A redux-thunk function that performs the asynchronous
 *      fetch operation.
 */
export const fetchAssignmentsCount = () => async (dispatch) => {
    dispatch(fetchRequest());
    await axios.get(`${config.API_BASE_URL}/api/v1/sims/assignment/count`, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => dispatch(getAssignmentsCount(res.data)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Fetches a list of deleted SIM cards from the API with pagination support.
 * This function dispatches actions to indicate the status of the API request
 *      and manage the received data or errors.
 *
 * @function
 * @param {number} [limit=10] - The number of deleted SIMs to fetch per page.
 * @param {number} [page=0] - The page index used to calculate the number of SIMs
 *                              to skip.
 * @returns {function} An asynchronous thunk function that performs the API request
 *                     and dispatches relevant actions.
 */
export const fetchDeletedSims = (limit = 10, page = 0) => async (dispatch) => {
    const skip = limit * page;
    dispatch(fetchRequest());
    await axios(
        `${config.API_BASE_URL}/api/v1/sims/deleted-sims?skip=${skip}&limit=${limit}`,
        {
            headers: {
                Authorization: config.getToken(),
            },
        },
    )
        .then((res) => dispatch(fetchTrashedSims(res)))
        .catch((error) => {
            dispatch(fetchFailure(error));
        });
};

/**
 * Generates a simulation by making an API request to the server and dispatching
 * the corresponding actions for request, success, and failure states.
 *
 * @param {Object} data - The payload data required for simulation generation.
 * @param {string} levelId - The ID of the level for which the simulation is generated.
 * @returns {Function} A thunk function that performs the asynchronous operation.
 */
export const generateSim = (data, levelId) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios.post(`${config.API_BASE_URL}/api/v1/sims/generate-sim?levelId=${levelId}`, data, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => dispatch(generateSimResponse(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};
